import { useEffect, useState } from "react";
import "../styles/presskit.css";

const STEAM_URL = "https://store.steampowered.com/app/3907080/Elacoria/";
const WEBSITE_URL = "https://elacoria.com";
const DISCORD_URL = "https://discord.gg/b2PwP23aH3";
const TRAILER_URL = "https://www.youtube.com/watch?v=UyU6hAp_1rA";
const CONTACT_EMAIL = "lightborn68studio@gmail.com";

export default function PressKit() {
  const [previewAsset, setPreviewAsset] = useState(null);

  const GMAIL_COMPOSE_URL = `https://mail.google.com/mail/?view=cm&fs=1&to=${CONTACT_EMAIL}`;

  async function handleCopyEmail() {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      alert("Email copied: " + CONTACT_EMAIL);
    } catch (error) {
      window.prompt("Copy this email:", CONTACT_EMAIL);
    }
  }

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setPreviewAsset(null);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <main className="presskit-page">
      <section className="presskit-hero">
        <p className="presskit-eyebrow">Official Press Kit</p>

        <h1>Elacoria Press Kit</h1>

        <p className="presskit-description">
          Elacoria is a 2D-looking open-world survival sandbox RPG with real 3D
          depth. Explore, build, farm, fight, and dig deep into an ever-changing
          world full of secrets.
        </p>

        <div className="presskit-actions">
          <a href={STEAM_URL} target="_blank" rel="noreferrer">
            Steam Page
          </a>

          <a href={TRAILER_URL} target="_blank" rel="noreferrer">
            Watch Trailer
          </a>

          <a href={GMAIL_COMPOSE_URL} target="_blank" rel="noreferrer">
            <button type="button" onClick={handleCopyEmail}>
              Contact
            </button>
          </a>
        </div>
      </section>

      <section className="presskit-section">
        <h2>Fact Sheet</h2>

        <div className="presskit-facts">
          <Fact label="Title" value="Elacoria" />
          <Fact label="Developer" value="Lightborn68" />
          <Fact label="Genre" value="Open-world Survival Sandbox RPG" />
          <Fact label="Platform" value="PC / Steam" />
          <Fact label="Engine" value="Unreal Engine 5" />
          <Fact label="Release Date" value="TBA" />
          <Fact label="Price" value="TBA" />
          <Fact label="Players" value="Singleplayer / Online Co-op Planned" />
        </div>
      </section>

      <section className="presskit-section">
        <h2>Description</h2>

        <p>
          Elacoria is an open-world survival sandbox RPG where players gather
          resources, craft tools, build shelters, farm crops, fight monsters,
          and explore a mysterious procedurally generated world.
        </p>

        <p>
          The game has a unique 2D visual style, but the world itself has real
          3D depth. Players can dig underground to discover caves, ores,
          dungeons, and bosses, or climb upward to reach floating islands.
        </p>
      </section>

      <section className="presskit-section">
        <h2>Key Features</h2>

        <ul className="presskit-list">
          <li>2D-looking visuals with a real 3D world underneath.</li>
          <li>
            Dig underground to discover caves, ores, dungeons, and bosses.
          </li>
          <li>
            Build homes, craft tools, farm crops, and automate production.
          </li>
          <li>Explore an infinite procedurally generated world.</li>
          <li>
            Fight monsters, bosses, and mysterious creatures called Elmons.
          </li>
          <li>Uncover the reason you were brought to Elacoria.</li>
        </ul>
      </section>

      <section className="presskit-section">
        <h2>Trailer</h2>

        <div className="presskit-video">
          <iframe
            src="https://www.youtube.com/embed/UyU6hAp_1rA"
            title="Elacoria Trailer"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <a
          className="presskit-link-button"
          href={TRAILER_URL}
          target="_blank"
          rel="noreferrer"
        >
          Open Trailer on YouTube
        </a>
      </section>

      <section className="presskit-section">
        <h2>Devlog</h2>

        <div className="presskit-video">
          <iframe
            src="https://www.youtube.com/embed/EAiTS7zfw-0"
            title="Elacoria Devlog"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <a
          className="presskit-link-button"
          href="https://www.youtube.com/watch?v=EAiTS7zfw-0&t=1987s"
          target="_blank"
          rel="noreferrer"
        >
          Watch Full Devlog on YouTube
        </a>
      </section>

      <section className="presskit-section">
        <h2>Logo</h2>

        <div className="presskit-placeholder-grid">
          <AssetBox
            title="Logo PNG"
            path="/press/logo/elacoria-logo.png"
            onPreview={setPreviewAsset}
          />

          <AssetBox
            title="Game Icon"
            path="/press/logo/elacoria-icon.png"
            onPreview={setPreviewAsset}
          />
        </div>
      </section>

      <section className="presskit-section">
        <h2>Screenshots</h2>

        <div className="presskit-placeholder-grid">
          {Array.from({ length: 18 }, (_, index) => {
            const number = String(index + 1).padStart(2, "0");

            return (
              <AssetBox
                key={number}
                title={`Screenshot ${number}`}
                path={`/press/screenshots/${number}.png`}
                onPreview={setPreviewAsset}
              />
            );
          })}
        </div>
      </section>

      <section className="presskit-section">
        <h2>Downloads</h2>

        <div className="presskit-links">
          <a href="/press/logo-pack.zip" target="_blank" rel="noreferrer">
            Download Logo Pack
          </a>

          <a href="/press/screenshots.zip" target="_blank" rel="noreferrer">
            Download Screenshots
          </a>

          <a
            href="/press/elacoria-press-kit.zip"
            target="_blank"
            rel="noreferrer"
          >
            Download Full Press Kit
          </a>
        </div>
      </section>

      <section className="presskit-section">
        <h2>Links & Contact</h2>

        <div className="presskit-links">
          <a href={STEAM_URL} target="_blank" rel="noreferrer">
            Steam
          </a>

          <a href={WEBSITE_URL} target="_blank" rel="noreferrer">
            Website
          </a>

          <a href={DISCORD_URL} target="_blank" rel="noreferrer">
            Discord
          </a>

          <a href={GMAIL_COMPOSE_URL} target="_blank" rel="noreferrer">
            {CONTACT_EMAIL}
          </a>
        </div>
      </section>

      {previewAsset && (
        <AssetPreviewModal
          asset={previewAsset}
          onClose={() => setPreviewAsset(null)}
        />
      )}
    </main>
  );
}

function Fact({ label, value }) {
  return (
    <div className="presskit-fact">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function AssetBox({ title, path, onPreview }) {
  function handlePreviewClick() {
    onPreview({
      title,
      path,
    });
  }

  return (
    <div className="presskit-asset-box">
      <button
        type="button"
        className="presskit-asset-image-button"
        onClick={handlePreviewClick}
        aria-label={`Preview ${title}`}
      >
        <img src={path} alt={title} className="presskit-asset-image" />
      </button>

      <div className="presskit-asset-footer">
        <span>{title}</span>

        <div className="presskit-asset-actions">
          <button type="button" onClick={handlePreviewClick}>
            Preview
          </button>

          <a href={path} download>
            Download
          </a>
        </div>
      </div>
    </div>
  );
}

function AssetPreviewModal({ asset, onClose }) {
  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }

  return (
    <div className="presskit-modal-backdrop" onClick={handleBackdropClick}>
      <div className="presskit-modal">
        <div className="presskit-modal-header">
          <h3>{asset.title}</h3>

          <button type="button" onClick={onClose} aria-label="Close preview">
            ×
          </button>
        </div>

        <div className="presskit-modal-image-wrap">
          <img src={asset.path} alt={asset.title} />
        </div>

        <div className="presskit-modal-footer">
          <code>{asset.path}</code>

          <a href={asset.path} download>
            Download
          </a>
        </div>
      </div>
    </div>
  );
}
